import { BotIcon, LoaderCircleIcon, UserIcon, WrenchIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
	type ContentPart,
	isText,
	isToolCall,
	isToolResult,
} from "./contentPart";
import { ToolCallEntry } from "./ToolCallEntry";
import type { ThreadChild } from "./types";

export interface MessageEntryProps {
	message: {
		_id: string;
		order: number;
		stepOrder: number;
		status: string;
		streaming?: boolean;
		text?: string;
		tool: boolean;
		agentName?: string;
		message?: {
			role: string;
			content: string | ContentPart[];
		};
	};
	toolResults: Map<string, unknown>;
	childThreads: Map<string, ThreadChild>;
}

export function MessageEntry({
	message,
	toolResults,
	childThreads,
}: MessageEntryProps) {
	const role = message.message?.role ?? "system";
	const content = message.message?.content;
	const isPending = message.status === "pending" || message.streaming;

	if (role === "user") {
		return <UserMessage text={message.text ?? ""} />;
	}

	if (role === "assistant") {
		const textParts =
			typeof content === "string"
				? [content]
				: (content ?? []).filter(isText).map((p) => p.text);
		const toolCalls =
			typeof content === "string" ? [] : (content ?? []).filter(isToolCall);
		const text =
			textParts.length > 0 ? textParts.join("\n") : (message.text ?? "");

		return (
			<AssistantMessage
				text={text}
				agentName={message.agentName}
				toolCalls={toolCalls}
				toolResults={toolResults}
				childThreads={childThreads}
				isPending={isPending}
			/>
		);
	}

	if (role === "tool") {
		const results =
			typeof content === "string" ? [] : (content ?? []).filter(isToolResult);
		if (results.length === 0) return null;
		// Tool results are rendered inline with their tool calls in AssistantMessage.
		// Only show standalone tool results that weren't matched.
		const unmatched = results.filter((r) => !toolResults.has(r.toolCallId));
		if (unmatched.length === 0) return null;

		return (
			<div className="space-y-2">
				{unmatched.map((r) => (
					<Card key={r.toolCallId} size="sm" className="border">
						<CardContent className="flex items-start gap-2">
							<WrenchIcon className="mt-0.5 size-4 text-muted-foreground" />
							<div className="min-w-0 flex-1">
								<p className="font-mono text-sm font-medium">{r.toolName}</p>
								<ScrollArea orientation="horizontal" className="mt-1">
									<pre className="whitespace-pre-wrap text-xs text-muted-foreground">
										{JSON.stringify(r.output ?? r.result, null, 2)}
									</pre>
								</ScrollArea>
							</div>
						</CardContent>
					</Card>
				))}
			</div>
		);
	}

	// System messages
	if (role === "system") {
		return (
			<Collapsible className="text-xs text-muted-foreground">
				<CollapsibleTrigger className="cursor-pointer italic hover:text-foreground">
					System message
				</CollapsibleTrigger>
				<CollapsibleContent>
					<pre className="mt-1 whitespace-pre-wrap rounded-md bg-muted p-2">
						{message.text ?? (typeof content === "string" ? content : "")}
					</pre>
				</CollapsibleContent>
			</Collapsible>
		);
	}

	return null;
}

function UserMessage({ text }: { text: string }) {
	return (
		<div className="flex items-start gap-3">
			<div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10">
				<UserIcon className="size-3.5 text-primary" />
			</div>
			<Card size="sm" className="flex-1 border bg-primary/5">
				<CardContent>
					<p className="whitespace-pre-wrap text-sm">{text}</p>
				</CardContent>
			</Card>
		</div>
	);
}

function AssistantMessage({
	text,
	agentName,
	toolCalls,
	toolResults,
	childThreads,
	isPending,
}: {
	text: string;
	agentName?: string;
	toolCalls: Array<{
		toolCallId: string;
		toolName: string;
		input?: unknown;
		args?: unknown;
	}>;
	toolResults: Map<string, unknown>;
	childThreads: Map<string, ThreadChild>;
	isPending?: boolean;
}) {
	return (
		<div className="flex items-start gap-3">
			<div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted">
				<BotIcon className="size-3.5 text-muted-foreground" />
			</div>
			<div
				className={cn(
					"min-w-0 flex-1",
					!text && toolCalls.length === 0 && "hidden",
				)}
			>
				{(text || agentName) && (
					<div className="space-y-1">
						{agentName && (
							<Badge variant="outline" className="text-xs">
								{agentName}
							</Badge>
						)}
						{text && (
							<p className="whitespace-pre-wrap text-sm">
								{text}
								{isPending && (
									<LoaderCircleIcon className="ml-1 inline size-3.5 animate-spin text-muted-foreground" />
								)}
							</p>
						)}
					</div>
				)}
				{toolCalls.map((tc) => (
					<ToolCallEntry
						key={tc.toolCallId}
						toolName={tc.toolName}
						input={tc.input ?? tc.args}
						result={toolResults.get(tc.toolCallId)}
						pending={!toolResults.has(tc.toolCallId)}
						childThreads={childThreads}
					/>
				))}
				{!text && toolCalls.length === 0 && isPending && (
					<LoaderCircleIcon className="size-4 animate-spin text-muted-foreground" />
				)}
			</div>
		</div>
	);
}
